import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select';
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { s } from '../../theme/tokens';
import { Box } from './Box';
import { IconButton } from './IconButton';

type RowsPerPageOption = number | { value: number; label: string };

type DisplayedRowsParams = {
  from: number;
  to: number;
  count: number;
  page: number;
};

type TablePaginationBarProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: readonly RowsPerPageOption[];
  disabled?: boolean;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  labelRowsPerPage?: ReactNode;
  labelDisplayedRows?: (paginationInfo: DisplayedRowsParams) => ReactNode;
  getItemAriaLabel?: (type: 'first' | 'last' | 'next' | 'previous') => string;
};

type ResolvedRowsPerPageOption = {
  value: number;
  label: string;
};

const PAGINATION_FONT_SIZE_PX = '15px';
const ROWS_PER_PAGE_DISABLE_THRESHOLD = 5;

const buildPageOptions = (pageCount: number): number[] =>
  Array.from({ length: pageCount }, (_value, index) => index);

const resolveDisplayedRows = (
  count: number,
  page: number,
  rowsPerPage: number,
): DisplayedRowsParams => {
  if (count === 0) {
    return { from: 0, to: 0, count: 0, page };
  }

  const from = page * rowsPerPage + 1;
  const to = count === -1 ? (page + 1) * rowsPerPage : Math.min(count, (page + 1) * rowsPerPage);

  return { from, to, count, page };
};

const defaultDisplayedRows = ({ count, from, to }: DisplayedRowsParams): string =>
  `${from}–${to} of ${count === -1 ? `more than ${to}` : count}`;

const defaultItemAriaLabel = (type: 'first' | 'last' | 'next' | 'previous'): string =>
  type === 'previous'
    ? 'Go to previous page'
    : type === 'next'
      ? 'Go to next page'
      : `Go to ${type} page`;

const resolveRowsPerPageOptions = (
  rowsPerPageOptions: readonly RowsPerPageOption[] | undefined,
): readonly ResolvedRowsPerPageOption[] =>
  (rowsPerPageOptions ?? []).map((option) =>
    typeof option === 'number' ? { value: option, label: String(option) } : option,
  );

export const TablePaginationBar = ({
  count,
  disabled = false,
  getItemAriaLabel = defaultItemAriaLabel,
  labelDisplayedRows = defaultDisplayedRows,
  labelRowsPerPage = 'Rows per page:',
  onPageChange,
  onRowsPerPageChange,
  page,
  rowsPerPage,
  rowsPerPageOptions,
}: TablePaginationBarProps): ReactElement => {
  const displayedRows = resolveDisplayedRows(count, page, rowsPerPage);
  const pageCount = count < 0 ? page + 1 : Math.max(1, Math.ceil(count / Math.max(1, rowsPerPage)));
  const pageOptions = buildPageOptions(pageCount);
  const resolvedRowsPerPageOptions = useMemo(
    () => resolveRowsPerPageOptions(rowsPerPageOptions),
    [rowsPerPageOptions],
  );
  const rowsPerPageSelectDisabled =
    disabled || (count >= 0 && count <= ROWS_PER_PAGE_DISABLE_THRESHOLD);
  const pageSelectDisabled = disabled || pageCount <= 1;
  const previousDisabled = disabled || page <= 0;
  const nextDisabled = disabled || (count >= 0 && page >= pageCount - 1);

  const handleRowsPerPageSelectChange = (event: SelectChangeEvent<number>): void => {
    onRowsPerPageChange?.(event.target.value);
  };

  const handlePageSelectChange = (event: SelectChangeEvent<number>): void => {
    const nextPage = event.target.value;
    if (!Number.isFinite(nextPage) || nextPage === page) {
      return;
    }

    onPageChange(null, nextPage);
  };

  return (
    <Box className="MuiTablePagination-bar">
      <Box className="MuiTablePagination-group MuiTablePagination-rowsPerPageGroup">
        <Box component="p" className="MuiTablePagination-label">
          {labelRowsPerPage}
        </Box>
        <MuiSelect<number>
          className="MuiTablePagination-selectInput"
          disabled={rowsPerPageSelectDisabled}
          disableUnderline
          variant="standard"
          value={rowsPerPage}
          onChange={handleRowsPerPageSelectChange}
          IconComponent={KeyboardArrowDownIcon}
          SelectDisplayProps={{ className: 'MuiTablePagination-selectValue' }}
          MenuProps={{
            slotProps: {
              paper: {
                sx: {
                  minWidth: '48px',
                  backgroundColor: (theme) => theme.app.colors.surface,
                  backgroundImage: 'none',
                  border: (theme) => `1px solid ${theme.app.colors.border}`,
                  borderRadius: (theme) => theme.app.radii.md,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '--Paper-overlay': 'none',
                  '--Paper-shadow': 'none',
                },
              },
              list: {
                role: 'listbox',
                sx: {
                  paddingTop: s('none'),
                  paddingBottom: s('none'),
                },
              },
            },
          }}
          sx={{
            minWidth: 0,
            fontFamily: (theme) => theme.typography.body.fontFamily,
            fontSize: PAGINATION_FONT_SIZE_PX,
            fontWeight: (theme) => theme.typography.body.fontWeight,
            lineHeight: (theme) => theme.typography.body.lineHeight,
            '& .MuiSelect-select': {
              paddingLeft: s('none'),
              fontFamily: (theme) => theme.typography.body.fontFamily,
              fontSize: PAGINATION_FONT_SIZE_PX,
              fontWeight: (theme) => theme.typography.body.fontWeight,
              lineHeight: (theme) => theme.typography.body.lineHeight,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
        >
          {resolvedRowsPerPageOptions.map((option) => (
            <MuiMenuItem
              key={option.value}
              value={option.value}
              className="MuiTablePagination-menuItem"
              sx={{
                color: (theme) => theme.app.colors.text,
                minHeight: s('4xl'),
                paddingTop: s('none'),
                paddingBottom: s('none'),
                paddingLeft: s('md'),
                paddingRight: s('md'),
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: (theme) => theme.typography.body.fontFamily,
                fontSize: PAGINATION_FONT_SIZE_PX,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                lineHeight: (theme) => theme.typography.body.lineHeight,
                backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
                border: '0 !important',
                cursor: 'pointer',
                '&&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'transparent !important',
                },
                '&&.Mui-selected:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&[aria-selected="true"]:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
              }}
            >
              {option.label}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </Box>
      <Box className="MuiTablePagination-group MuiTablePagination-pageGroup">
        <Box component="p" className="MuiTablePagination-label">
          Page:
        </Box>
        <MuiSelect<number>
          className="MuiTablePagination-selectInput"
          disabled={pageSelectDisabled}
          disableUnderline
          variant="standard"
          value={page}
          onChange={handlePageSelectChange}
          IconComponent={KeyboardArrowDownIcon}
          SelectDisplayProps={{ className: 'MuiTablePagination-selectValue' }}
          MenuProps={{
            slotProps: {
              paper: {
                sx: {
                  minWidth: '48px',
                  backgroundColor: (theme) => theme.app.colors.surface,
                  backgroundImage: 'none',
                  border: (theme) => `1px solid ${theme.app.colors.border}`,
                  borderRadius: (theme) => theme.app.radii.md,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '--Paper-overlay': 'none',
                  '--Paper-shadow': 'none',
                },
              },
              list: {
                role: 'listbox',
                sx: {
                  paddingTop: s('none'),
                  paddingBottom: s('none'),
                },
              },
            },
          }}
          sx={{
            minWidth: 0,
            fontFamily: (theme) => theme.typography.body.fontFamily,
            fontSize: PAGINATION_FONT_SIZE_PX,
            fontWeight: (theme) => theme.typography.body.fontWeight,
            lineHeight: (theme) => theme.typography.body.lineHeight,
            '& .MuiSelect-select': {
              paddingLeft: s('none'),
              fontFamily: (theme) => theme.typography.body.fontFamily,
              fontSize: PAGINATION_FONT_SIZE_PX,
              fontWeight: (theme) => theme.typography.body.fontWeight,
              lineHeight: (theme) => theme.typography.body.lineHeight,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
        >
          {pageOptions.map((pageOption) => (
            <MuiMenuItem
              key={pageOption}
              value={pageOption}
              className="MuiTablePagination-menuItem"
              sx={{
                color: (theme) => theme.app.colors.text,
                minHeight: s('4xl'),
                paddingTop: s('none'),
                paddingBottom: s('none'),
                paddingLeft: s('md'),
                paddingRight: s('md'),
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: (theme) => theme.typography.body.fontFamily,
                fontSize: PAGINATION_FONT_SIZE_PX,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                lineHeight: (theme) => theme.typography.body.lineHeight,
                backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
                border: '0 !important',
                cursor: 'pointer',
                '&&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'transparent !important',
                },
                '&&.Mui-selected:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&[aria-selected="true"]:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
              }}
            >
              {pageOption + 1}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </Box>
      <Box className="MuiTablePagination-displayedRows">{labelDisplayedRows(displayedRows)}</Box>
      <Box className="MuiTablePagination-navigation">
        <IconButton
          aria-label={getItemAriaLabel('previous')}
          color="inherit"
          disabled={previousDisabled}
          onClick={(event) => {
            onPageChange(event, page - 1);
          }}
          title={getItemAriaLabel('previous')}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton
          aria-label={getItemAriaLabel('next')}
          color="inherit"
          disabled={nextDisabled}
          onClick={(event) => {
            onPageChange(event, page + 1);
          }}
          title={getItemAriaLabel('next')}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
